import { H4 } from "@/shared/components/typography";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/shared/components/ui/button";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { useToast } from "@/shared/hooks/use-toast";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { RequirementAdditionalInformation } from "@/services/backend/qpm/requirements/types/requirement";
import { ReqAdditionalInformationDTO } from "@/services/backend/qpm/requirements/types/requirement-additional-information";
import {
  useQpmCertificateCustomerAddMutation,
  useQpmCertificateCustomerListQuery,
  useQpmCertificateCustomerRemoveMutation,
  useQpmCertificateHttpCreateMutation,
  useQpmCertificateHttpListQuery,
} from "@/services/backend/qpm/certificates/api/certificate-api";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/shared/components/ui/command";
import { ChevronsUpDown, X } from "lucide-react";
import { useActiveCompany } from "@/shared/lib/active-company/active-company-provider";

export type CertificateComponentProps = {
  additionalInformation: ReqAdditionalInformationDTO;
  customerId: string;
  onUpdate: (updatedInfo: Partial<RequirementAdditionalInformation>) => void;
};

export function CertificatesComponent({
  additionalInformation,
  customerId,
  onUpdate,
}: CertificateComponentProps) {
  const { toast } = useToast();
  const { companyId } = useActiveCompany();

  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const [additionalComment, setAdditionalComment] = useState<string>(
    additionalInformation.certificateNotes,
  );

  const {
    data: qpmCertificateList,
    error: errorCertificates,
    isLoading: isLoadingCertificates,
  } = useQpmCertificateHttpListQuery({
    company: { active: true, values: [companyId] },
  });

  const {
    data: customerCertificates,
    error: errorCustomerCertificates,
    isLoading: isLoadingCustomerCertificates,
  } = useQpmCertificateCustomerListQuery({ customerId });

  const [createCertificate, createState] =
    useQpmCertificateHttpCreateMutation();

  const [addCustomerCertificate, addState] =
    useQpmCertificateCustomerAddMutation();

  const [removeCustomerCertificate, removeState] =
    useQpmCertificateCustomerRemoveMutation();

  useEffect(() => {
    const err = createState.error || addState.error || removeState.error;
    if (!err) {
      return;
    }
    toast({ ...parseRTKQueryError(err) });
    createState.reset();
    addState.reset();
    removeState.reset();
  }, [addState, createState, removeState, toast]);

  const selectedIds = useMemo(
    () => new Set(customerCertificates?.items.map((c) => c.id) ?? []),
    [customerCertificates?.items],
  );

  const availableCertificates = useMemo(() => {
    const items = qpmCertificateList?.items ?? [];
    return items.filter((it) => !selectedIds.has(it.id));
  }, [qpmCertificateList?.items, selectedIds]);

  const handleAddExisting = async (certificateId: string) => {
    await addCustomerCertificate({ customerId, certificateId }).unwrap();
    setSearchValue("");
    setOpen(false);
  };

  const handleCreateAndAdd = async () => {
    const name = searchValue.trim();
    if (!name) {
      return;
    }

    const created = await createCertificate({
      companyId,
      name,
      alwaysDisplay: true,
    }).unwrap();

    await addCustomerCertificate({
      customerId,
      certificateId: created.id,
    }).unwrap();

    setSearchValue("");
    setOpen(false);
  };

  const handleRemove = async (certificateId: string) => {
    await removeCustomerCertificate({ customerId, certificateId }).unwrap();
  };

  const onChangeCertificateText = (value: string) => {
    setAdditionalComment(value);
    onUpdate({ certificateNotes: value });
  };

  if (isLoadingCertificates || isLoadingCustomerCertificates) {
    return <CertificateSkeleton />;
  }

  if (errorCertificates) {
    return <RTKQueryErrorAlert error={errorCertificates} />;
  }

  if (errorCustomerCertificates) {
    return <RTKQueryErrorAlert error={errorCustomerCertificates} />;
  }

  return (
    <div className="flex flex-col gap-2 border-b py-2">
      <H4>Zertifikate</H4>

      <div className="flex flex-wrap items-center gap-2">
        {(customerCertificates?.items ?? []).map((cert) => (
          <div
            key={cert.id}
            className="bg-muted flex items-center gap-1 rounded-md border px-2 py-1"
          >
            <div className="text-sm">{cert.name}</div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => {
                handleRemove(cert.id).catch(() => undefined);
              }}
              title="Entfernen"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}

        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="min-w-65 justify-between"
            >
              Zertifikat wählen oder anlegen
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="min-w-65 p-0" side="bottom" align="start">
            <Command>
              <CommandInput
                placeholder="Suchen oder anlegen..."
                value={searchValue}
                onValueChange={setSearchValue}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleCreateAndAdd().catch(() => undefined);
                  }
                }}
              />
              <CommandList>
                <CommandEmpty>
                  <div className="flex flex-col gap-3 px-6 text-sm">
                    <span>Kein Zertifikat gefunden.</span>
                    <Button
                      type="button"
                      onClick={() => {
                        handleCreateAndAdd().catch(() => undefined);
                      }}
                      disabled={!searchValue.trim()}
                    >
                      Hinzufügen
                    </Button>
                  </div>
                </CommandEmpty>

                <CommandGroup heading="Verfügbare Zertifikate">
                  {availableCertificates.map((cert) => (
                    <CommandItem
                      key={cert.id}
                      onSelect={() => {
                        handleAddExisting(cert.id).catch(() => undefined);
                      }}
                    >
                      {cert.name}
                    </CommandItem>
                  ))}
                </CommandGroup>

                <CommandSeparator />
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      <div className="flex flex-wrap">
        <Label htmlFor="additionalCertificateText" className="mb-2">
          Sonstige
        </Label>
        <Textarea
          id="additionalCertificateText"
          placeholder="Sonstige"
          value={additionalComment}
          onChange={(e) => {
            onChangeCertificateText(e.target.value);
          }}
        />
      </div>
    </div>
  );
}

function CertificateSkeleton() {
  return (
    <div className="flex flex-col gap-2 border-b py-2">
      <H4>Zertifikate</H4>
      <div className="flex flex-wrap justify-start gap-3">
        <Skeleton className="h-8 w-full" />
      </div>
      <div className="flex flex-wrap">
        <Label htmlFor="additionalCertificateText" className="mb-2">
          Sonstige
        </Label>
        <Textarea id="additionalCertificateText" placeholder="Sonstige" />
      </div>
    </div>
  );
}
